// Data repository — the single seam between the UI and the data source.
//
// Reads published content from Firestore (project a-homely-abode). The public
// site only ever sees docs with status === "published" (enforced again by
// firestore.rules). Collections are tiny, so we filter by status server-side
// and sort client-side — this avoids composite-index requirements for the
// public read path.
//
// Static brand content (services, marketing, framework, enquiry types) is not
// CMS-managed and stays in mockData.js.

import { db } from "@/lib/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import * as mock from "@/data/mockData";

const ok = (data) => Promise.resolve(data);
const mapDoc = (d) => ({ id: d.id, ...d.data() });
const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

async function publishedDocs(name) {
  const snap = await getDocs(
    query(collection(db, name), where("status", "==", "published"))
  );
  return snap.docs.map(mapDoc);
}

/** A single published doc by id (slug). Returns null if missing/not published. */
async function publishedDocById(name, id) {
  try {
    const snap = await getDoc(doc(db, name, id));
    if (!snap.exists()) return null;
    const data = mapDoc(snap);
    return data.status === "published" ? data : null;
  } catch (e) {
    // Rules deny reading a non-published doc for anonymous users → treat as 404.
    if (e?.code === "permission-denied") return null;
    throw e;
  }
}

/* ----------------------------- listings ----------------------------- */
export async function getListings() {
  return (await publishedDocs("listings")).sort(byOrder);
}
export function getListingBySlug(slug) {
  return publishedDocById("listings", slug);
}

/* -------------------------- proven results -------------------------- */
export async function getProvenResults() {
  return (await publishedDocs("provenResults")).sort(byOrder);
}
export function getProvenResultById(id) {
  return publishedDocById("provenResults", id);
}

/* --------------------------- testimonials --------------------------- */
export async function getTestimonials() {
  // Newest published date first (latest → oldest); explicit order breaks ties.
  return (await publishedDocs("testimonials")).sort(
    (a, b) => String(b.date || "").localeCompare(String(a.date || "")) || byOrder(a, b)
  );
}

/* ------------------------------ awards ------------------------------ */
export async function getAwards() {
  // Newest award date first (falls back to year), then explicit order.
  const key = (x) => x.awardDate || x.year || "";
  return (await publishedDocs("awards")).sort(
    (a, b) => String(key(b)).localeCompare(String(key(a))) || byOrder(a, b)
  );
}

/* ------------------------------ blogs ------------------------------- */
export async function getBlogs() {
  const blogs = await publishedDocs("blogs");
  // Newest first by publishAt (Firestore Timestamp → millis).
  return blogs.sort(
    (a, b) => (b.publishAt?.toMillis?.() ?? 0) - (a.publishAt?.toMillis?.() ?? 0)
  );
}
export function getBlogBySlug(slug) {
  return publishedDocById("blogs", slug);
}
export async function getBlogCategories() {
  const blogs = await getBlogs();
  return ["All", ...Array.from(new Set(blogs.map((b) => b.category).filter(Boolean)))];
}

/* ---- Static brand content (not CMS-managed; stays in mockData) ----- */
export const getServices = () => ok(mock.SERVICES);
export const getMarketing = () => ok(mock.MARKETING);
export const getFrameworkSteps = () => ok(mock.FRAMEWORK_STEPS);
export const getFrameworkIntro = () => ok(mock.FRAMEWORK_INTRO);
export const getEnquiryTypes = () => ok(mock.ENQUIRY_TYPES);
